import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { apiPermits, apiSites, apiRefs } from '@/lib/api';
import { PERMIT_TYPES, DEFAULT_RISQUES, DEFAULT_MESURES, DEFAULT_EPI } from '@/constants/permits';
import { PERMIT_FORM } from '@/constants/testIds';
import RisksBlocks from '@/components/RisksBlocks';
import RequiredPermitsBlock from '@/components/RequiredPermitsBlock';
import AdtEpiSection from '@/components/AdtEpiSection';
import AdtCorrespondants from '@/components/AdtCorrespondants';
import PermisFeuForm from '@/components/permits/PermisFeuForm';
import PermisLevageForm from '@/components/permits/PermisLevageForm';
import PermisHauteurForm from '@/components/permits/PermisHauteurForm';
import PermisNacelleForm from '@/components/permits/PermisNacelleForm';
import PermisEspaceConfineForm from '@/components/permits/PermisEspaceConfineForm';
import AttestationConsignationElecForm from '@/components/permits/AttestationConsignationElecForm';
import PermisFouilleForm from '@/components/permits/PermisFouilleForm';
import PermisBordureEauForm from '@/components/permits/PermisBordureEauForm';
import BonLototoForm from '@/components/permits/BonLototoForm';
import InheritedFromATBanner from '@/components/permits/InheritedFromATBanner';
import ATTemplatesBar from '@/components/ATTemplatesBar';
import Combobox from '@/components/ui/Combobox';
import { EmergencyBanner, OrganigrammeBloc, InterferencesBloc, SignatureEngagementText } from '@/components/AdtExtras';
import BlocW from '@/components/BlocW';
import { ArrowLeft, Save, Send, AlertTriangle, Plus, X, Trash2 } from 'lucide-react';

function SopStepsTable({ steps, setSteps }) {
  const ensureMin = (arr) => {
    const items = [...arr];
    while (items.length < 5) {
      items.push({ sous_etapes: '', risques_mesures: '' });
    }
    return items;
  };
  const list = ensureMin(steps || []);
  const update = (i, k, v) => {
    const next = [...list];
    next[i] = { ...next[i], [k]: v };
    setSteps(next);
  };
  const addRow = () => setSteps([...list, { sous_etapes: '', risques_mesures: '' }]);
  const removeRow = (i) => setSteps(list.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="label-industrial">Décomposition de l'opération (D — minimum 5 étapes)</label>
      <div className="overflow-x-auto border border-stone-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-stone-100 border-b-2 border-stone-300">
              <th className="text-left p-2 w-24 font-bold uppercase tracking-wider text-[10px]">Étape</th>
              <th className="text-left p-2 font-bold uppercase tracking-wider text-[10px]">D1 — Sous-étapes clés à réaliser en analysant la SOP (Lafarge ou de l'entreprise)</th>
              <th className="text-left p-2 font-bold uppercase tracking-wider text-[10px]">D2 — Risques et mesures de prévention associées</th>
              <th className="w-8 p-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((step, i) => (
              <tr key={i} className="border-b border-stone-200 align-top">
                <td className="p-2 font-bold bg-stone-50">Étape {i + 1}</td>
                <td className="p-1">
                  <textarea
                    data-testid={`sop-step-${i}-d1`}
                    rows={3}
                    className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm resize-y"
                    value={step.sous_etapes || ''}
                    onChange={(e) => update(i, 'sous_etapes', e.target.value)}
                    placeholder="Décrire la sous-étape précise du chantier"
                  />
                </td>
                <td className="p-1">
                  <textarea
                    data-testid={`sop-step-${i}-d2`}
                    rows={3}
                    className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm resize-y"
                    value={step.risques_mesures || ''}
                    onChange={(e) => update(i, 'risques_mesures', e.target.value)}
                    placeholder="Risques identifiés + mesures de prévention"
                  />
                </td>
                <td className="p-1 text-center">
                  {list.length > 5 && (
                    <button type="button" onClick={() => removeRow(i)} className="text-[#DA291C] hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        data-testid="sop-add-step"
        onClick={addRow}
        className="mt-3 text-xs font-bold uppercase tracking-wider text-[#09090B] inline-flex items-center gap-1"
      >
        <Plus className="w-4 h-4" /> Ajouter une étape
      </button>
    </div>
  );
}

function BonIESection({ extra, setExtra, replaceExtra }) {
  const mode = extra.bie_mode || 'manuel';
  const lines = extra.lines || [
    { equipement: '', dispositif_isolement: '', energie: '', etat_to: '', test_out: '' },
    { equipement: '', dispositif_isolement: '', energie: '', etat_to: '', test_out: '' },
    { equipement: '', dispositif_isolement: '', energie: '', etat_to: '', test_out: '' },
    { equipement: '', dispositif_isolement: '', energie: '', etat_to: '', test_out: '' },
    { equipement: '', dispositif_isolement: '', energie: '', etat_to: '', test_out: '' },
  ];

  const updateLine = (i, k, v) => {
    const next = lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l));
    setExtra('lines', next);
  };
  const addLine = () => setExtra('lines', [...lines, { equipement: '', dispositif_isolement: '', energie: '', etat_to: '', test_out: '' }]);
  const removeLine = (i) => setExtra('lines', lines.filter((_, idx) => idx !== i));

  return (
    <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 space-y-4">
      <div className="flex items-center gap-3">
        <span className="label-industrial !mb-0">Mode :</span>
        <button type="button" data-testid="bie-mode-pced"
          onClick={() => replaceExtra({ bie_mode: 'pced' })}
          className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-2 ${mode === 'pced' ? 'border-[#09090B] bg-stone-100' : 'border-stone-300'}`}>
          PCED rattachée
        </button>
        <button type="button" data-testid="bie-mode-manuel"
          onClick={() => replaceExtra({ bie_mode: 'manuel' })}
          className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-2 ${mode === 'manuel' ? 'border-[#09090B] bg-stone-100' : 'border-stone-300'}`}>
          Lignes manuelles
        </button>
      </div>

      {mode === 'pced' ? (
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-industrial">Numéro PCED *</label>
            <input className="input-industrial w-full"
              data-testid="bie-pced-numero"
              value={extra.pced_numero || ''}
              onChange={(e) => setExtra('pced_numero', e.target.value)}
              placeholder="Ex. SPLX.675-PA50" />
          </div>
          <div>
            <label className="label-industrial">Date création PCED</label>
            <input type="date" className="input-industrial w-full"
              data-testid="bie-pced-date"
              value={extra.pced_date || ''}
              onChange={(e) => setExtra('pced_date', e.target.value)} />
          </div>
          <div>
            <label className="label-industrial">Description tâche PCED</label>
            <input className="input-industrial w-full"
              data-testid="bie-pced-desc"
              value={extra.pced_description || ''}
              onChange={(e) => setExtra('pced_description', e.target.value)} />
          </div>
        </div>
      ) : (
        <div>
          <label className="label-industrial">Sources d'énergie à isoler (minimum 5 lignes)</label>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead className="bg-stone-100 border-2 border-stone-200">
                <tr>
                  <th className="text-left p-2 w-8">#</th>
                  <th className="text-left p-2">Équipement (EQ)</th>
                  <th className="text-left p-2">Dispositif d'isolement (DI) + accessoires</th>
                  <th className="text-left p-2">Énergie</th>
                  <th className="text-left p-2">État énergétique à zéro (TO)</th>
                  <th className="text-left p-2">TEST-OUT</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-stone-200">
                    <td className="p-1 font-mono text-stone-500">{i + 1}</td>
                    <td className="p-1">
                      <input className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm"
                        data-testid={`bie-line-${i}-eq`}
                        value={l.equipement} onChange={(e) => updateLine(i, 'equipement', e.target.value)} />
                    </td>
                    <td className="p-1">
                      <input className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm"
                        data-testid={`bie-line-${i}-di`}
                        value={l.dispositif_isolement} onChange={(e) => updateLine(i, 'dispositif_isolement', e.target.value)} />
                    </td>
                    <td className="p-1">
                      <select className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm"
                        data-testid={`bie-line-${i}-energie`}
                        value={l.energie} onChange={(e) => updateLine(i, 'energie', e.target.value)}>
                        <option value="">—</option>
                        <option>Électrique</option>
                        <option>Pneumatique</option>
                        <option>Hydraulique</option>
                        <option>Mécanique</option>
                        <option>Thermique</option>
                        <option>Chimique</option>
                        <option>Gravitaire</option>
                      </select>
                    </td>
                    <td className="p-1">
                      <input className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm"
                        data-testid={`bie-line-${i}-to`}
                        value={l.etat_to} onChange={(e) => updateLine(i, 'etat_to', e.target.value)} />
                    </td>
                    <td className="p-1">
                      <input className="w-full px-2 py-2 border border-stone-200 focus:border-[#09090B] outline-none text-sm"
                        data-testid={`bie-line-${i}-testout`}
                        value={l.test_out} onChange={(e) => updateLine(i, 'test_out', e.target.value)}
                        placeholder="OK / Date" />
                    </td>
                    <td className="p-1 text-center">
                      {lines.length > 5 && (
                        <button type="button" onClick={() => removeLine(i)} className="text-[#DA291C] hover:text-red-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button"
            data-testid="bie-add-line"
            onClick={addLine}
            className="mt-3 text-xs font-bold uppercase tracking-wider text-[#09090B] inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Ajouter une ligne
          </button>
        </div>
      )}
    </div>
  );
}

function ChipInput({ label, items, setItems, suggestions = [], testid, datalistId = null }) {
  const [input, setInput] = useState('');
  const add = (val) => {
    const v = val.trim();
    if (v && !items.includes(v)) setItems([...items, v]);
    setInput('');
  };
  return (
    <div>
      <label className="label-industrial">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-stone-100 border border-stone-300 px-3 py-1.5 text-xs font-semibold text-[#09090B]">
            {it}
            <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>
              <X className="w-3 h-3 hover:text-[#DA291C]" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
          placeholder="Ajouter..."
          className="input-industrial flex-1"
          data-testid={testid}
          list={datalistId || undefined}
        />
        <button type="button" onClick={() => add(input)} className="btn-industrial-secondary !min-h-[56px]">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {suggestions.length > 0 && items.length === 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <span className="text-[10px] uppercase font-bold text-stone-500 tracking-wider mr-1">Suggestions:</span>
          {suggestions.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => add(s)}
              className="text-[11px] px-2 py-0.5 border border-stone-300 text-stone-700 hover:bg-stone-100"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PermitNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const [sites, setSites] = useState([]);
  const [refs, setRefs] = useState({ companies: [], equipments: [], operations: [], inductions: [], pdp: [], energies: [], lafarge_contacts: [] });
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [type, setType] = useState(params.get('type') || '');
  const [form, setForm] = useState({
    site_code: '',
    titre: '',
    description: '',
    lieu: '',
    date_debut: '',
    date_fin: '',
    entreprise_exterieure: '',
    intervenants: [],
    risques_identifies: [],
    mesures_prevention: [],
    epi_requis: [],
    extra: {},
  });

  useEffect(() => {
    apiSites.list().then(r => setSites(r.data)).catch(() => {});
    apiRefs.all().then(r => setRefs((prev) => ({ ...prev, ...r.data }))).catch(() => {});
    apiRefs.lafargeContacts().then(r => setRefs((prev) => ({ ...prev, lafarge_contacts: r.data }))).catch(() => {});
  }, []);

  // Edit mode — load existing permit
  useEffect(() => {
    if (!editId) return;
    setLoadingEdit(true);
    apiPermits.get(editId).then(({ data }) => {
      setType(data.type);
      // Auto-fill Resp Lafarge / Resp EE depuis les correspondants AT pour les permis EC enfants
      const extra = { ...(data.extra || {}) };
      if (extra.corresp_lafarge_nom && !extra.resp_lafarge) {
        extra.resp_lafarge = extra.corresp_lafarge_nom;
      }
      if (extra.corresp_st_nom && !extra.resp_ee) {
        extra.resp_ee = extra.corresp_st_nom;
      }
      // Pour le Permis Espace Confiné : pré-cocher par défaut les 4 cases "À réaliser au préalable"
      if (data.type === 'permis_espace_confine') {
        if (extra.prep_consignation_matiere === undefined) extra.prep_consignation_matiere = true;
        if (extra.prep_balisage === undefined) extra.prep_balisage = true;
        if (extra.prep_affichage === undefined) extra.prep_affichage = true;
        if (extra.prep_restriction === undefined) extra.prep_restriction = true;
      }
      setForm({
        site_code: data.site_code || '',
        titre: data.titre || '',
        description: data.description || '',
        lieu: data.lieu || '',
        date_debut: data.date_debut || '',
        date_fin: data.date_fin || '',
        entreprise_exterieure: data.entreprise_exterieure || '',
        intervenants: data.intervenants || [],
        risques_identifies: data.risques_identifies || [],
        mesures_prevention: data.mesures_prevention || [],
        epi_requis: data.epi_requis || [],
        extra,
        linked_permits: data.linked_permits || [],
        parent_id: data.parent_id || null,
        parent_status: data.parent_status || null,
        parent_numero: data.parent_numero || null,
      });
    }).catch(() => {
      toast.error('Permis introuvable');
      navigate('/permits');
    }).finally(() => setLoadingEdit(false));
  }, [editId]); // eslint-disable-line

  // Re-fetch equipments/operations/energies when site changes
  useEffect(() => {
    if (form.site_code) {
      Promise.all([
        apiRefs.equipments(form.site_code),
        apiRefs.operations(undefined, form.site_code),
        apiRefs.energies(undefined, undefined, form.site_code),
      ]).then(([eq, op, en]) => {
        setRefs((prev) => ({ ...prev, equipments: eq.data, operations: op.data, energies: en.data }));
      }).catch(() => {});
    }
  }, [form.site_code]);

  // When entreprise changes → load PdP + inductions list
  const selectedCompany = refs.companies.find((c) => c.name === form.entreprise_exterieure);
  const currentPdp = refs.pdp.find((p) => p.entreprise === form.entreprise_exterieure);
  const filteredInductions = refs.inductions.filter((i) =>
    !form.entreprise_exterieure || i.entreprise === form.entreprise_exterieure
  );

  // Auto-fill operation when equipement (lieu) selected
  const matchedOperations = refs.operations.filter((o) => o.equipement === form.lieu);
  const matchedEnergies = refs.energies.filter((e) =>
    e.equipement === form.lieu && form.extra.operation && e.operation === form.extra.operation
  );

  useEffect(() => {
    if (type) {
      setForm((f) => ({
        ...f,
        risques_identifies: f.risques_identifies.length ? f.risques_identifies : [],
        mesures_prevention: f.mesures_prevention.length ? f.mesures_prevention : [],
        epi_requis: f.epi_requis.length ? f.epi_requis : [],
      }));
    }
  }, [type]);

  // Pour Permis Espace Confiné en création : pré-cocher les 4 cases "À réaliser au préalable"
  useEffect(() => {
    if (editId) return;
    if (type === 'permis_espace_confine') {
      setForm((f) => ({
        ...f,
        extra: {
          ...f.extra,
          prep_consignation_matiere: f.extra.prep_consignation_matiere ?? true,
          prep_balisage: f.extra.prep_balisage ?? true,
          prep_affichage: f.extra.prep_affichage ?? true,
          prep_restriction: f.extra.prep_restriction ?? true,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Auto-fill pp_numero depuis le référentiel PdP de l'entreprise (uniquement si pas déjà saisi)
  useEffect(() => {
    if (currentPdp?.numero && !form.extra.pp_numero) {
      setForm((f) => ({ ...f, extra: { ...f.extra, pp_numero: currentPdp.numero } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPdp?.numero]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setExtra = (k, v) => setForm((f) => ({ ...f, extra: { ...f.extra, [k]: v } }));

  // Nombre total d'étapes selon le type de permis (AT = 5, permis complexes détaillés = 3, autres = 3)
  const DETAILED_TYPES = ['permis_feu', 'permis_levage', 'permis_hauteur', 'permis_nacelle', 'permis_espace_confine', 'attestation_consignation_elec', 'permis_fouille', 'permis_bordure_eau', 'bon_isolation_energies'];
  // Permis enfant d'une AT → identification + intervenants sont hérités du parent, on n'affiche que la partie spécifique
  const isChildDetailed = !!form.parent_id && DETAILED_TYPES.includes(type);
  const totalSteps = type === 'autorisation_travail' ? 5 : (isChildDetailed ? 1 : 3);
  const detailedStepLabel = isChildDetailed ? '1 / 1' : '3 / 3';

  const submit = async (status = 'brouillon') => {
    if (!type) return toast.error('Choisissez le type de permis');
    if (!form.site_code) return toast.error('Sélectionnez un site');
    if (!form.titre) return toast.error('Le titre est obligatoire');
    if (!form.lieu) return toast.error('Le lieu est obligatoire');
    if (!form.date_debut || !form.date_fin) return toast.error('Renseignez les dates');

    setSubmitting(true);
    try {
      let permitId = editId;
      if (editId) {
        // Mode édition — PATCH
        await apiPermits.update(editId, form);
      } else {
        // Création
        const { data } = await apiPermits.create({ ...form, type });
        permitId = data.id;
      }
      if (status === 'soumis') {
        await apiPermits.changeStatus(permitId, 'soumis');
      }
      toast.success(
        editId
          ? (status === 'soumis' ? 'Permis modifié et soumis' : 'Modifications enregistrées')
          : (status === 'soumis' ? 'Permis soumis' : 'Brouillon enregistré')
      );
      navigate(`/permits/${permitId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <button
        data-testid={PERMIT_FORM.backButton}
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-stone-600 hover:text-[#09090B]"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
          {editId ? 'Modification' : 'Nouveau document'}
        </div>
        <h1 className="text-4xl font-display font-black tracking-tighter">
          {editId ? 'Modifier le permis' : 'Créer un permis'}
        </h1>
        {loadingEdit && <p className="text-stone-500 text-sm mt-2">Chargement du permis...</p>}
      </div>

      {/* Type selector — masqué en mode édition */}
      {!editId && (
      <div className="section-card space-y-5">
        {/* AT mise en avant — point d'entrée principal */}
        <div>
          <label className="label-industrial">Document principal</label>
          <div className="grid grid-cols-1 mt-3">
            {PERMIT_TYPES.filter((t) => t.id === 'autorisation_travail').map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={PERMIT_FORM.typeSelector(t.id)}
                onClick={() => setType(t.id)}
                className={`text-left p-5 border-2 transition-all ${
                  type === t.id ? 'border-[#09090B] bg-stone-50' : 'border-stone-300 hover:border-stone-500'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`inline-block text-xs font-bold uppercase tracking-wider px-2.5 py-1 ${t.color} border`}>
                    {t.short}
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-display font-bold leading-tight">{t.label}</div>
                    <div className="text-sm text-stone-600 mt-1 leading-snug">{t.description}</div>
                    <div className="text-[11px] text-stone-500 mt-2 italic">
                      Les permis complexes (Feu, Levage, Consignation Élec, Bon LOTOTO, etc.) seront ajoutés automatiquement selon les risques cochés.
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Autres permis — standalone (rarement utilisés, accessibles dans une section repliable) */}
        <details className="border-t border-stone-200 pt-4">
          <summary className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 cursor-pointer hover:text-stone-700">
            Créer un permis complexe standalone (sans Autorisation de Travail)
          </summary>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
            {PERMIT_TYPES.filter((t) => t.id !== 'autorisation_travail').map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={PERMIT_FORM.typeSelector(t.id)}
                onClick={() => setType(t.id)}
                className={`text-left p-4 border-2 transition-all ${
                  type === t.id ? 'border-[#09090B] bg-stone-50' : 'border-stone-200 hover:border-stone-400'
                }`}
              >
                <div className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 ${t.color} border`}>
                  {t.short}
                </div>
                <div className="text-sm font-display font-bold mt-3 leading-tight">{t.label}</div>
                <div className="text-[11px] text-stone-600 mt-1.5 leading-snug">{t.description}</div>
              </button>
            ))}
          </div>
        </details>
      </div>
      )}

      {type && (
        <>
          {/* Barre Modèles favoris — uniquement pour les AT */}
          {type === 'autorisation_travail' && (
            <ATTemplatesBar
              currentAtId={editId}
              currentAtTitre={form.titre}
            />
          )}

          {/* Section 1: Identification — masquée pour les permis détaillés ENFANTS d'une AT (héritage parent) */}
          {!isChildDetailed && (
          <div className="section-card space-y-4">
            <div>
              <div className="label-industrial">Étape 1 / {totalSteps}</div>
              <h2 className="text-2xl font-display font-bold tracking-tight">
                {type === 'autorisation_travail' ? 'A — Informations concernant l\'intervention' : 'Identification'}
              </h2>
            </div>

            {/* Bloc Correspondants déplacé après Site + Titre du permis */}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label-industrial">Site *</label>
                <select
                  data-testid={PERMIT_FORM.inputSite}
                  className="input-industrial w-full"
                  value={form.site_code}
                  onChange={(e) => set('site_code', e.target.value)}
                >
                  <option value="">— Sélectionner —</option>
                  {sites.map((s) => (
                    <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-industrial">Titre du permis *</label>
                <input
                  data-testid={PERMIT_FORM.inputTitre}
                  type="text"
                  className="input-industrial w-full"
                  value={form.titre}
                  onChange={(e) => set('titre', e.target.value)}
                  placeholder="Ex. Maintenance broyeur cru ligne 1"
                />
              </div>
            </div>

            {/* Entreprise extérieure — positionné juste sous Site/Titre */}
            <div>
              <label className="label-industrial">Entreprise extérieure (si applicable)</label>
              <Combobox
                value={form.entreprise_exterieure}
                onChange={(v) => set('entreprise_exterieure', v)}
                options={refs.companies.map((c) => ({
                  value: c.name,
                  label: c.name,
                  sublabel: [c.activite, c.representant].filter(Boolean).join(' • '),
                }))}
                placeholder="Liste depuis Coordonnées EE (Travaux internes si vide)"
                testid={PERMIT_FORM.inputEntreprise}
                className="input-industrial w-full"
                emptyMessage="Aucune entreprise correspondante"
              />
              {(selectedCompany || currentPdp) && (
                <div className="mt-3 p-3 bg-stone-50 border border-stone-200 grid sm:grid-cols-2 gap-2 text-xs">
                  {currentPdp && (
                    <>
                      <div><span className="font-bold uppercase tracking-wider text-stone-500">N° PdP :</span> <span className="font-mono">{currentPdp.numero}</span></div>
                      <div><span className="font-bold uppercase tracking-wider text-stone-500">Validité :</span> {currentPdp.validite || '—'}</div>
                    </>
                  )}
                  {selectedCompany && (
                    <>
                      {selectedCompany.activite && <div><span className="font-bold uppercase tracking-wider text-stone-500">Activité :</span> {selectedCompany.activite}</div>}
                      {selectedCompany.representant && <div><span className="font-bold uppercase tracking-wider text-stone-500">Représentant :</span> {selectedCompany.representant}</div>}
                      {selectedCompany.rep_portable && <div><span className="font-bold uppercase tracking-wider text-stone-500">Portable :</span> {selectedCompany.rep_portable}</div>}
                      {selectedCompany.rep_mail && <div><span className="font-bold uppercase tracking-wider text-stone-500">Email :</span> {selectedCompany.rep_mail}</div>}
                    </>
                  )}
                </div>
              )}
              {form.entreprise_exterieure && (
                <div className="mt-3">
                  <label className="label-industrial">N° Plan de Prévention</label>
                  <input
                    className="input-industrial w-full"
                    data-testid={PERMIT_FORM.extraField('pp_numero')}
                    value={form.extra.pp_numero || (currentPdp?.numero || '')}
                    onChange={(e) => setExtra('pp_numero', e.target.value)}
                    placeholder="Ex. PP-2026-0123 (auto-rempli si PdP existe)"
                  />
                </div>
              )}
            </div>

            {/* Bloc Correspondants + Commande WorkOrder + Service prescripteur + vCard QR — ADT uniquement */}
            {type === 'autorisation_travail' && (
              <AdtCorrespondants
                extra={form.extra}
                setExtra={setExtra}
                site={sites.find((s) => s.code === form.site_code)}
                lafargeContacts={refs.lafarge_contacts}
                selectedCompany={selectedCompany}
              />
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-industrial">Lieu précis / Équipement *</label>
                <Combobox
                  value={form.lieu}
                  onChange={(v) => set('lieu', v)}
                  options={refs.equipments}
                  placeholder="Ex. Atelier broyeur 2 — Niveau +12m (autocomplete depuis base RMP)"
                  testid={PERMIT_FORM.inputLieu}
                  className="input-industrial w-full"
                  emptyMessage="Aucun équipement correspondant — saisie libre possible"
                  maxResults={30}
                />
                {matchedOperations.length > 0 && (
                  <div className="mt-2">
                    <label className="label-industrial">Type d'opération (auto)</label>
                    <select
                      data-testid="permit-operation-auto"
                      className="input-industrial w-full"
                      value={form.extra.operation || ''}
                      onChange={(e) => setExtra('operation', e.target.value)}
                    >
                      <option value="">— Sélectionner une opération —</option>
                      {matchedOperations.map((o, i) => (
                        <option key={i} value={o.operation}>{o.operation}</option>
                      ))}
                    </select>
                  </div>
                )}
                {form.lieu && matchedOperations.length === 0 && (
                  <div className="mt-2">
                    <label className="label-industrial">Type d'opération (saisie manuelle)</label>
                    <input
                      data-testid="permit-operation-manual"
                      type="text"
                      className="input-industrial w-full"
                      value={form.extra.operation || ''}
                      onChange={(e) => setExtra('operation', e.target.value)}
                      placeholder="Aucun équipement RMP correspondant — saisir l'opération"
                    />
                  </div>
                )}

                {/* ARED — Tableau C : Risques connus à ce jour & mesures de prévention */}
                {type === 'autorisation_travail' && matchedEnergies.length > 0 && (
                  <div className="mt-4 border border-stone-200">
                    <div className="bg-[#09090B] text-white px-4 py-2">
                      <div className="text-[11px] font-bold uppercase tracking-wider">
                        C — Risques connus à ce jour & mesures de prévention (Base ARED)
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="text-left p-2 w-32">Énergies dangereuses</th>
                            <th className="text-left p-2 w-16">Valide</th>
                            <th className="text-left p-2">Description</th>
                            <th className="text-left p-2 w-24">Risque résiduel</th>
                            <th className="text-left p-2">Mesures de prévention</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matchedEnergies.map((e, i) => (
                            <tr key={i} className="border-b border-stone-100 align-top">
                              <td className="p-2 font-semibold">{e.type_energie}</td>
                              <td className="p-2">
                                <input type="checkbox"
                                  data-testid={`ared-valid-${i}`}
                                  checked={true}
                                  disabled
                                  readOnly
                                  className="w-4 h-4 accent-[#DA291C] cursor-not-allowed" />
                              </td>
                              <td className="p-2 text-stone-700 whitespace-pre-wrap">{e.description || '—'}</td>
                              <td className="p-2 text-stone-700">
                                {e.risque_residuel || (e.probabilite_residuelle && e.severite_residuelle ? `P${e.probabilite_residuelle}×S${e.severite_residuelle}` : '—')}
                              </td>
                              <td className="p-2 text-stone-700 whitespace-pre-wrap">{e.mesures || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="label-industrial">Date de début *</label>
                <input
                  data-testid={PERMIT_FORM.inputDateDebut}
                  type="datetime-local"
                  className="input-industrial w-full"
                  value={form.date_debut}
                  onChange={(e) => set('date_debut', e.target.value)}
                />
              </div>
              <div>
                <label className="label-industrial">Date de fin *</label>
                <input
                  data-testid={PERMIT_FORM.inputDateFin}
                  type="datetime-local"
                  className="input-industrial w-full"
                  value={form.date_fin}
                  onChange={(e) => set('date_fin', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-industrial">Description des travaux</label>
                <textarea
                  data-testid={PERMIT_FORM.inputDescription}
                  rows={4}
                  className="input-industrial w-full !min-h-[120px] py-3"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Décrire précisément la nature et l'étendue des travaux"
                />
              </div>
            </div>
          </div>
          )}

          {/* Section 2: Intervenants & spécifique — masquée pour les permis détaillés ENFANTS */}
          {!isChildDetailed && (
          <div className="section-card space-y-4">
            <div>
              <div className="label-industrial">Étape 2 / {totalSteps}</div>
              <h2 className="text-2xl font-display font-bold tracking-tight">Intervenants & spécificités</h2>
            </div>

            <ChipInput
              label="Personnes intervenantes (depuis Accueils sécurité)"
              items={form.intervenants}
              setItems={(v) => set('intervenants', v)}
              testid={PERMIT_FORM.inputIntervenants}
              datalistId="intervenants-options"
            />
            <datalist id="intervenants-options">
              {filteredInductions.map((i, idx) => (
                <option key={idx} value={`${i.name}${i.role ? ' — ' + i.role : ''}`} />
              ))}
            </datalist>

            {/* Anciens blocs simples permis_feu / permis_levage retirés —
                ils sont désormais remplacés par les composants détaillés
                PermisFeuForm / PermisLevageForm rendus plus bas (template fidèle Lafarge). */}

            {/* Bon LOTOTO — rendu pleine page via BonLototoForm + BonIESection (tableau) */}
          </div>
          )}

          {/* Bandeau "Auto-importé depuis l'AT" — affiché en haut de chaque permis détaillé enfant */}
          {isChildDetailed && (
            <div className="section-card">
              <InheritedFromATBanner
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                ppNumero={form.extra.pp_numero}
                ppValidite={form.extra.pp_validite}
                ppStatus={form.extra.pp_status}
                parentId={form.parent_id}
                parentStatus={form.parent_status}
                parentNumero={form.parent_numero}
              />
            </div>
          )}

          {type === 'bon_isolation_energies' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Bon d'Isolation Énergétique (LOTOTO)</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Bon d'Isolation Énergétique — LOTOTO</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Procédure de consignation des sources d'énergie en 6 étapes (Lock-Out / Tag-Out / Try-Out).
                </p>
              </div>
              <BonLototoForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              >
                <BonIESection
                  extra={form.extra}
                  setExtra={setExtra}
                  replaceExtra={(patch) => setForm({ ...form, extra: { ...form.extra, ...patch } })}
                />
              </BonLototoForm>
            </div>
          )}

          {/* Section 3: HSE — masquée pour les permis détaillés (couvert par leur formulaire dédié) */}
          {!DETAILED_TYPES.includes(type) && (
          <div className="section-card space-y-6">
            <div>
              <div className="label-industrial">Étape 3 / {totalSteps}</div>
              <h2 className="text-2xl font-display font-bold tracking-tight">Analyse HSE</h2>
            </div>

            {type === 'autorisation_travail' ? (
              <SopStepsTable
                steps={form.extra.sop_steps || []}
                setSteps={(v) => setExtra('sop_steps', v)}
              />
            ) : (
              <>
                <ChipInput
                  label="Risques identifiés"
                  items={form.risques_identifies}
                  setItems={(v) => set('risques_identifies', v)}
                  suggestions={DEFAULT_RISQUES[type] || []}
                  testid={PERMIT_FORM.inputRisques}
                />
                <ChipInput
                  label="Mesures de prévention"
                  items={form.mesures_prevention}
                  setItems={(v) => set('mesures_prevention', v)}
                  suggestions={DEFAULT_MESURES[type] || []}
                  testid={PERMIT_FORM.inputMesures}
                />
              </>
            )}
            <ChipInput
              label="EPI requis"
              items={form.epi_requis}
              setItems={(v) => set('epi_requis', v)}
              suggestions={type === 'autorisation_travail' ? [] : (DEFAULT_EPI[type] || [])}
              testid={PERMIT_FORM.inputEpi}
            />

            {type === 'autorisation_travail' && (
              <div className="mt-6">
                <div className="label-industrial mb-3">EPI minimum + spécifiques (sections N / O / P / Q)</div>
                <AdtEpiSection
                  value={form.extra.adt_epi || {}}
                  onChange={(v) => setExtra('adt_epi', v)}
                />
              </div>
            )}

          </div>
          )}

          {/* Section spécifique au TYPE permis_feu (apparaît si le permis enfant est un PF) */}
          {type === 'permis_feu' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis de Feu</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Détails du Permis de Feu</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Renseignez les éléments spécifiques au Permis de Feu : types de points chauds, matériel, risques particuliers, moyens d'extinction.
                </p>
              </div>
              <PermisFeuForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section spécifique au TYPE permis_levage */}
          {type === 'permis_levage' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis de Levage</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Détails du Permis de Levage</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Renseignez les caractéristiques de la charge, le moyen de levage, l'élingage et la zone d'exclusion.
                </p>
              </div>
              <PermisLevageForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section spécifique au TYPE permis_hauteur */}
          {type === 'permis_hauteur' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis Travail en Hauteur</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Détails du Permis de Travail en Hauteur</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Renseignez la hiérarchie des contrôles, les systèmes de retenue/stop-chute, le tirant d'air et le plan de secours.
                </p>
              </div>
              <PermisHauteurForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section spécifique au TYPE permis_nacelle */}
          {type === 'permis_nacelle' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis Nacelle</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Détails du Permis Nacelle (PEMP)</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Type de nacelle, ancrage, check-list 8 points obligatoire et plan de secours.
                </p>
              </div>
              <PermisNacelleForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section spécifique au TYPE permis_espace_confine */}
          {type === 'permis_espace_confine' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis Espace Confiné</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Permis d'entrer en Espace Confiné</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Analyse de l'atmosphère, moyens de prévention, ventilation, surveillance et plan de secours EC.
                </p>
              </div>
              <PermisEspaceConfineForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
                correspondantLafarge={form.extra.corresp_lafarge_nom || ''}
                correspondantEE={form.extra.corresp_st_nom || ''}
              />
            </div>
          )}

          {/* Section spécifique au TYPE attestation_consignation_elec */}
          {type === 'attestation_consignation_elec' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Attestation de Consignation Électrique</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Attestation de Consignation Électrique</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Consignation en 2 étapes suivant NFC 18-510 (séparation + condamnation, puis VAT + MAT).
                </p>
              </div>
              <AttestationConsignationElecForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section spécifique au TYPE permis_fouille */}
          {type === 'permis_fouille' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis de Fouille</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Permis de Fouille</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Nature des dangers, précautions, protections collectives, consultations obligatoires/facultatives, coordinateur fouilles + exploitant.
                </p>
              </div>
              <PermisFouilleForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section spécifique au TYPE permis_bordure_eau */}
          {type === 'permis_bordure_eau' && (
            <div className="section-card">
              <div className="mb-3">
                <div className="label-industrial">Étape {detailedStepLabel} — Spécifique Permis Travail en Bordure d'Eau</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Permis Travail en Bordure d'Eau</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Check-list double validation (émetteur + validateur), dangers spécifiques bordure d'eau et mesures correctives.
                </p>
              </div>
              <PermisBordureEauForm
                extra={form.extra}
                setExtra={setExtra}
                dateDebut={form.date_debut}
                dateFin={form.date_fin}
                titre={form.titre}
                lieu={form.lieu}
                entreprise={form.entreprise_exterieure}
                compact={isChildDetailed}
                siteCode={form.site_code}
              />
            </div>
          )}

          {/* Section 3 bis: Blocs E-M (Risques majeurs ADT) */}
          {type === 'autorisation_travail' && (
            <div className="section-card space-y-4">
              <div>
                <div className="label-industrial">Étape 4 / 5</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Blocs Risques Majeurs (E à O)</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Cochez les cases applicables. La case <strong>OUI</strong> du bloc s'active automatiquement et le permis associé sera requis.
                </p>
              </div>
              <RisksBlocks
                value={form.extra.risk_blocks || {}}
                onChange={(v) => setExtra('risk_blocks', v)}
              />

              {/* Section "Permis complexes nécessaires" — apparaît dynamiquement */}
              <div className="border-t-2 border-stone-200 pt-5 mt-2">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#DA291C] mb-3">
                  Permis complexes nécessaires
                </div>
                <RequiredPermitsBlock
                  blocsRisques={form.extra.risk_blocks || {}}
                  linkedPermits={form.linked_permits || []}
                  parentId={editId}
                  entrepriseExterieure={form.entreprise_exterieure}
                  onOpen={async (linked, parentAtId, ptype) => {
                    if (linked) {
                      const ret = parentAtId ? `/permits/new?edit=${parentAtId}` : '/permits';
                      navigate(`/permits/${linked.id}?return=${encodeURIComponent(ret)}`);
                      return;
                    }
                    // Pas encore en DB : il faut sauver l'AT d'abord pour créer les enfants
                    if (!form.site_code || !form.titre || !form.lieu || !form.date_debut || !form.date_fin) {
                      toast.error("Veuillez d'abord remplir Site, Titre, Lieu et Dates avant de compléter les permis complexes");
                      return;
                    }
                    setSubmitting(true);
                    try {
                      let atId = editId;
                      let refreshed;
                      if (editId) {
                        const { data } = await apiPermits.update(editId, form);
                        refreshed = data;
                      } else {
                        const { data } = await apiPermits.create({ ...form, type });
                        atId = data.id;
                        // Re-fetch pour récupérer les linked_permits
                        const { data: full } = await apiPermits.get(atId);
                        refreshed = full;
                      }
                      const child = (refreshed.linked_permits || []).find((p) => p.type === ptype);
                      if (child) {
                        toast.success('Brouillon enregistré — ouverture du permis complexe');
                        const ret = `/permits/new?edit=${atId}`;
                        navigate(`/permits/${child.id}?return=${encodeURIComponent(ret)}`);
                      } else {
                        toast.error('Permis complexe introuvable');
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Bloc S — Organigramme + Bloc T — Interférences (ADT uniquement) */}
          {type === 'autorisation_travail' && (
            <div className="section-card space-y-6">
              <div>
                <div className="label-industrial">Étape 5 / 5</div>
                <h2 className="text-2xl font-display font-bold tracking-tight">Organisation & Interférences</h2>
              </div>
              {form.site_code && (
                <EmergencyBanner site={sites.find((s) => s.code === form.site_code)} />
              )}
              <OrganigrammeBloc
                value={form.extra.organigramme || {}}
                onChange={(v) => setExtra('organigramme', v)}
                correspondantLafarge={form.extra.corresp_lafarge_nom || ''}
                entrepriseExterieure={form.entreprise_exterieure || ''}
                correspondantEE={form.extra.corresp_st_nom || ''}
              />
              <InterferencesBloc
                value={form.extra.interferences || []}
                onChange={(v) => setExtra('interferences', v)}
              />
              <BlocW
                value={form.extra.bloc_w || {}}
                onChange={(v) => setExtra('bloc_w', v)}
                linkedPermitTypes={Object.entries(form.extra.risk_blocks || {})
                  .filter(([, arr]) => Array.isArray(arr) && arr.some(Boolean))
                  .flatMap(([blockId]) => {
                    const map = { F: ['permis_feu'], G: ['permis_levage'], H: ['permis_hauteur', 'permis_nacelle'], I: ['permis_espace_confine'] };
                    return map[blockId] || [];
                  })}
              />

              {/* Validation accueil sécurité SST N2 */}
              {(() => {
                const sstN2 = (form.extra.organigramme?.sst_niveau2 || []).filter((s) => s?.entreprise);
                if (sstN2.length === 0) return null;
                return (
                  <div className="border-2 border-amber-300 bg-amber-50 p-3 space-y-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
                      Vérification accueils sécurité — SST Niveau 2
                    </div>
                    {sstN2.map((s, i) => {
                      const found = refs.inductions.filter((x) => x.entreprise.toUpperCase() === s.entreprise.toUpperCase() && x.valid);
                      const ok = found.length > 0;
                      return (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="font-semibold">{s.entreprise}</span>
                          <span className={`font-bold uppercase tracking-wider px-2 py-0.5 ${ok ? 'bg-green-600 text-white' : 'bg-[#DA291C] text-white'}`}>
                            {ok ? `✓ ${found.length} intervenants accueillis` : '⚠ Aucun accueil sécurité trouvé'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Section 4: Action */}
          <div className="section-card flex items-center justify-between gap-4 flex-wrap bg-stone-50">
            <div className="flex items-start gap-3 max-w-md">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-stone-700 leading-relaxed">
                Les signatures et l'approbation se feront depuis la fiche du permis après création.
                Vous pouvez enregistrer en brouillon et compléter plus tard.
              </div>
            </div>
            <div className="flex gap-3">
              <button
                data-testid={PERMIT_FORM.submitDraft}
                disabled={submitting}
                onClick={() => submit('brouillon')}
                className="btn-industrial-secondary flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> {editId ? 'Enregistrer modifications' : 'Enregistrer brouillon'}
              </button>
              {!editId && (
                <button
                  data-testid={PERMIT_FORM.submitSubmit}
                  disabled={submitting}
                  onClick={() => submit('soumis')}
                  className="btn-industrial-primary flex items-center gap-2"
                >
                  <Send className="w-4 h-4" /> Soumettre pour révision
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
